/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {memo, useEffect, useRef, useState} from 'react';
import {Collapse, IconButton, List, ListItemButton, Typography} from '@mui/material';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {theme} from '../../../constants/theme';
import {themesMap} from './constants';
import {ArgosSettingsIcon} from '../Icons/ArgosActionIcons';
import ArgosFlexbox from '../Containers/ArgosFlexbox';


/*
 * Argos Collapsible Component
 * Takes in a map with componentType, data, dialogState props. 
 * - Component type can be used to index the themesMap in constants.js, 
    if none is provided no theme will be applied. 
 * - Data is a map where the keys are the values to be displayed
    in the header of the list and values are the components of the sublist. 
 * -DialogState determines some style decisions (based on whether the app is in 
    full screen or dialog 
  * -StyleProps determines some style decisions (based on whether the app is in 
    full screen or dialog )
 */
const ArgosCollapseComponent = memo((props: any) => {
  const [styleProps, setStyleProps] = useState<any>();
  const [open, setOpen] = useState<number[]>([]);
  const [config, setConfig] = useState<number[]>([]);

  const initialized = useRef(false);

  useEffect(() => {
    // This is only for what's open by default. So only run once (but don't run until data is available and it's not empty)
    if (initialized.current) {
      return
    }
    if (props.data != null && Object.keys(props.data).length) {
      initialized.current = true
    } else {
      return
    }
    if (props.defaultOpenKeys != undefined) {
      // defaultOpenKeys is a list of strings that are the keys of the data. convert to list of indices
      const openIndices: number[] = []
      props.defaultOpenKeys.forEach((element: string) => {
        openIndices.push(Object.keys(props.data).indexOf(element))
      })
      setOpen(openIndices)
    } else {
      setOpen(props.defaultOpen ? [...Array(Object.keys(props.data).length).keys()] : [])
    }
  }, [props.defaultOpen, props.defaultOpenKeys, props.data]);

  useEffect(() => {
    populateStyleProps()
    setConfig(props?.config != undefined ? props?.config : [])
  }, [props?.styleProps, props?.config])

  const populateStyleProps = () => {
    if (props?.styleProps != undefined) {
      const _props = props?.styleProps
      const localStyleProps: any = {}
      if (_props?.fontColor != undefined) {
        localStyleProps["fontColor"] = _props?.fontColor
      }
      if (_props?.outlineOnly != undefined) {

        localStyleProps["outlineOnly"] = _props?.outlineOnly
        localStyleProps["backgroundColor"] = "white"
        localStyleProps["borderColor"] = localStyleProps["fontColor"]
        localStyleProps["border"] = "2px solid"
      }
      if (_props?.backgroundColor != undefined) {
        localStyleProps["backgroundColor"] = _props?.backgroundColor
      }
      setStyleProps(localStyleProps);
    }
  }

  const handleClick = (clickedIndex: number) => {
    let isOpening = false;
    if (open.includes(clickedIndex)) {
      const openCopy = open.filter((element) => {
        return element !== clickedIndex
      });
      setOpen(openCopy);
    } else {
      const openCopy = [...open];
      openCopy.push(clickedIndex);
      isOpening = true
      setOpen(openCopy);
    }
    if (props?.onClickListItem) {
      // get the key of the clicked index
      const clickedKey = Object.keys(props.data)[clickedIndex]
      props.onClickListItem(clickedKey, isOpening)
    }
    if (props?.setCurrentOpenKeysList) {
      // convert the open list to a list of keys, and add the key of the clicked index
      const openKeys = open.map((index) => {
        return Object.keys(props.data)[index]
      })
      if (isOpening) {
        openKeys.push(Object.keys(props.data)[clickedIndex])
      } else {
        openKeys.splice(openKeys.indexOf(Object.keys(props.data)[clickedIndex]), 1)
      }
      props.setCurrentOpenKeysList(openKeys)
    }
  }

  const isListItemDisabled = (_headerElement: string) => {
    return props?.data[_headerElement]?.props?.disableResults ?? false
  }
  const getListItemTitle = (_headerElement: string) => {
    return _headerElement + (isListItemDisabled(_headerElement) ? props?.disableResultsText ?? "" : "")
  }

  return (
    <List
      sx={{
        width: '100%', overflow: "auto", paddingTop: "0px", height: '100%', scrollbarGutter: 'stable'
      }}
      component="nav"
      aria-labelledby="nested-list-subheader"
    >
      {Object.keys(props.data).map((headerElement, mapIndex: number) => {
          return (
            <div key={mapIndex} style={{
              width: "100%", display: "flex", flexDirection: "column",
              border: "2px solid",
              borderColor: theme.layout.header.backgroundColor,
              borderRadius: 10,
              marginBottom: "20px",
            }}>
              <ArgosFlexbox style={{justifyContent: "space-between", width: "100%"}}>
                <ArgosFlexbox
                  onClick={() => handleClick(mapIndex)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    flexGrow: 1,
                    justifyContent: "left",
                    alignItems: "center"
                  }}
                >
                  <Typography
                    style={{
                      color: styleProps?.fontColor ?? theme.layout.sidebar.backgroundColor,
                      borderColor: styleProps?.fontColor,
                      paddingLeft: 10
                    }}
                    onClick={() => handleClick(mapIndex)}
                  >
                    {getListItemTitle(headerElement)}
                  </Typography>
                </ArgosFlexbox>
                <ArgosFlexbox style={{justifyContent: "right", alignItems: "center"}}>
                  {props?.config != undefined && props.onClickConfig != undefined && config.includes(mapIndex) && <IconButton
                    style={{
                      color: styleProps?.fontColor,
                      borderColor: styleProps?.fontColor,
                    }}
                    onClick={() => {
                      props.onClickConfig()
                    }}
                  >
                    <ArgosSettingsIcon/>
                  </IconButton>}
                  <ListItemButton
                    disabled={isListItemDisabled(headerElement)}
                    style={{
                      color: styleProps?.fontColor ?? theme.layout.sidebar.backgroundColor,
                      borderColor: styleProps?.fontColor
                    }}
                    onClick={() => handleClick(mapIndex)}
                  >
                    {open.includes(mapIndex) ? <ExpandLess/> : <ExpandMore/>}
                  </ListItemButton>
                </ArgosFlexbox>


              </ArgosFlexbox>
              <ThemeProvider theme={themesMap[props.componentType] ?? createTheme({})}>
                <Collapse
                  key={mapIndex}
                  in={!isListItemDisabled(headerElement) && open.includes(mapIndex)}
                  timeout="auto"
                  // unmountOnExit // TODO: do we want this?
                >
                  {props.data[headerElement]}
                </Collapse>
              </ThemeProvider>
            </div>
          )
        }
      )}
    </List>
  )
})

export default ArgosCollapseComponent;